import { Button, FormField, SelectInput, styled, TextInput } from 'canoe';
import { debounce } from 'lodash';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

const Form = styled.form`
  padding: 12px;
  flex-direction: column;
  border-radius: 4px;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  justify-content: flex-end;
`;

type Item = { label: string; value: string };

export interface VariableFormProps {
  onSubmit: (e: any) => void;
  name: string;
  setName: (name: string) => void;
  options: Item[];
  fallback: string;
  setFallback: (fallback: string) => void;
  onClose: () => void;
  isSubmitEnabled?: boolean;
  submitText?: string;
  cancelText?: string;
}

export const VariableForm = ({
  onSubmit,
  name,
  setName,
  options,
  fallback,
  setFallback,
  onClose,
  isSubmitEnabled = true,
  submitText = 'Submit',
  cancelText = 'Cancel',
}: VariableFormProps) => {
  const [searchValue, setSearchValue] = useState<string>('');

  const updateSearchValue = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    }, 300),
    [],
  );

  const clearSearchValue = useCallback(() => {
    setSearchValue('');
  }, []);

  const items = useMemo(() => {
    const newOptions: Item[] = [...options];

    const valueExists = name && options?.find((option) => option.value === name);

    if (!valueExists && name) {
      newOptions.push({
        label: name,
        value: name,
      });
    }

    const found =
      searchValue &&
      newOptions.find(({ label }: Item) => label.toLowerCase() === searchValue?.toLowerCase());

    const formattedOptions = [
      ...(!found && searchValue?.trim()
        ? [
            {
              label: searchValue.trim(),
              value: searchValue.trim(),
            },
          ]
        : []),
      ...newOptions,
    ] as Item[];

    return !searchValue
      ? formattedOptions
      : formattedOptions.filter((option: Item) =>
          option.value.toLowerCase().includes(searchValue.toLowerCase()),
        );
  }, [searchValue, options, name]);

  return (
    <Form onSubmit={onSubmit}>
      <FormField label="VARIABLES" scale="small">
        <SelectInput<Item>
          selectedItem={items.find((option) => option.value === name)}
          scale="small"
          clearable={false}
          onSelect={(selected) => {
            setName(selected?.value ?? '');
          }}
          placeholder="Select Variable"
          items={items}
          searchable
          onFilter={updateSearchValue}
          onClearFilter={clearSearchValue}
          searchInputProps={{
            placeholder: 'Search or Create',
          }}
        />
      </FormField>
      <FormField label="FALLBACK" scale="small">
        <TextInput
          scale="small"
          value={fallback}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFallback(e.target.value)}
        />
      </FormField>
      <ButtonContainer>
        <Button
          appearance="text"
          type="button"
          size="small"
          onClick={() => onClose()}
          variant="white"
        >
          {cancelText}
        </Button>
        <Button
          darkBackground
          appearance="outline"
          variant="secondary"
          size="small"
          ml="xs"
          type="submit"
          disabled={!isSubmitEnabled}
        >
          {submitText}
        </Button>
      </ButtonContainer>
    </Form>
  );
};
